import {
  type RefObject,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'

import type { NodeOverlay } from './canvasTypes'
import type { GraphLiveAppearance } from './graphInstrumentation'
import {
  type ClientPoint,
  hitTestClientPoint,
  listenForNodeOverlayInspection,
} from './nodeOverlayInspection'

export type NodeOverlayInspection = {
  overlay: NodeOverlay
  anchor: ClientPoint
  bounds: { left: number; top: number; width: number; height: number }
}
export function useNodeOverlayInspection(
  overlays: NodeOverlay[],
  svgRef: RefObject<SVGSVGElement>,
  containerRef: RefObject<HTMLDivElement>,
  liveValueAppearances: GraphLiveAppearance[]
): {
  inspection: NodeOverlayInspection | null
  activeNodeIds: string[]
  suspend: () => void
  dismiss: () => void
} {
  const [inspection, setInspection] = useState<NodeOverlayInspection | null>(
    null
  )
  const appearances = useRef(liveValueAppearances)
  useLayoutEffect(() => {
    appearances.current = liveValueAppearances
  }, [liveValueAppearances])
  const pointer = useRef<ClientPoint | null>(null)
  const dragging = useRef(false)
  const paused = useRef(false)
  const dismissed = useRef(false)
  const timer = useRef<ReturnType<typeof setTimeout>>()
  const frame = useRef<number>()
  const cancelPending = useCallback((): void => {
    clearTimeout(timer.current)
    timer.current = undefined
    if (frame.current !== undefined) cancelAnimationFrame(frame.current)
    frame.current = undefined
  }, [])

  const showHit = useCallback(
    (overlay: NodeOverlay | undefined, anchor: ClientPoint): void => {
      const bounds = containerRef.current?.getBoundingClientRect()
      if (!overlay || !bounds) {
        setInspection(null)
        return
      }
      setInspection({
        overlay,
        anchor,
        bounds: {
          left: bounds.left,
          top: bounds.top,
          width: bounds.width,
          height: bounds.height,
        },
      })
    },
    [containerRef]
  )

  const evaluatePointer = useCallback((): void => {
    frame.current = undefined
    if (dragging.current || paused.current || !pointer.current) return
    const hit = hitTestClientPoint(
      overlays,
      svgRef.current,
      pointer.current,
      appearances.current
    )
    if (!hit) return
    const { overlay } = hit
    if (dismissed.current) {
      if (!overlay) dismissed.current = false
      return
    }
    showHit(overlay, pointer.current)
  }, [overlays, showHit, svgRef])

  const suspend = useCallback((): void => {
    setInspection(null)
    cancelPending()
    if (!overlays.length) return
    paused.current = true
    timer.current = setTimeout((): void => {
      timer.current = undefined
      paused.current = false
      evaluatePointer()
    }, 160)
  }, [cancelPending, evaluatePointer, overlays.length])

  const dismiss = useCallback((): void => {
    cancelPending()
    pointer.current = null
    paused.current = false
    dismissed.current = true
    setInspection(null)
  }, [cancelPending])

  useLayoutEffect(() => {
    setInspection(null)
    dismissed.current = false
    const container = containerRef.current
    const removeListeners =
      container && overlays.length > 0
        ? listenForNodeOverlayInspection(container, {
            move: (point: ClientPoint, buttons: number): void => {
              pointer.current = point
              if (buttons) {
                suspend()
                return
              }
              if (frame.current === undefined)
                frame.current = requestAnimationFrame(evaluatePointer)
            },
            leave: (): void => {
              dismiss()
              dismissed.current = false
            },
            down: (): void => {
              dragging.current = true
              suspend()
            },
            up: (): void => {
              dragging.current = false
              suspend()
            },
            isDragging: (): boolean => dragging.current,
            escape: dismiss,
            suspend,
          })
        : undefined
    return (): void => {
      removeListeners?.()
      cancelPending()
      pointer.current = null
      dragging.current = false
      paused.current = false
      dismissed.current = false
    }
  }, [cancelPending, containerRef, dismiss, evaluatePointer, overlays, suspend])

  return {
    inspection,
    suspend,
    dismiss,
    activeNodeIds: inspection ? [inspection.overlay.node.id] : [],
  }
}
